import React, { useState, useEffect, useRef } from "react";
import "./summarypage.css";
import "./UpdateGoal.css";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { faCircleExclamation, faSliders, faLocationDot, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import Toast from "./Toast";

//images
import PEE from "../assets/PictureEE.jpg";
import PHP from "../assets/PictureHP.jpg";
import PSC from "../assets/PictureSC.jpg";
import PPC from "../assets/PicturePC.jpg";
import PP from "../assets/PictureP.jpg";
import PEQ from "../assets/Harry_Stills38.jpg";
import { API_BASE, assetUrl } from "../apiConfig";
import { buildDomainTagsFromGoal } from "./goalDomainTags";
import { withNewestGoalVersion } from "./newestGoalVersion";

// ---------------------------------------------------------------------------
// Intervention description rendering
//
// intervention_description is authored in a rich text editor, so an entry can
// carry formatting markup: <b>/<strong>, <i>/<em>, <u>, <a href>, <br>, lists,
// <p> ... The "Download Summary" button snapshots the LIVE DOM with
// html2canvas, so any markup that is printed as plain text on screen is also
// plain text in the PDF. Rendering the markup (allow-listed, so nothing
// executable comes through) in both the on-screen and the PDF branch is what
// makes bold / italic / underline / links / line breaks survive the download.
// ---------------------------------------------------------------------------

// Tags kept when sanitizing. Anything else is unwrapped (its text survives,
// the tag does not), so unknown/legacy markup can never break the layout.
const ALLOWED_TAGS = new Set([
  "a", "b", "blockquote", "br", "code", "del", "div", "em", "h1", "h2", "h3",
  "h4", "h5", "h6", "hr", "i", "ins", "li", "mark", "ol", "p", "pre", "s",
  "small", "span", "strike", "strong", "sub", "sup", "u", "ul",
]);

// Inline style properties kept, so pasted content that expresses bold/italic
// through a style attribute (instead of <b>/<i>) still renders.
const ALLOWED_STYLE_PROPS = new Set([
  "background-color", "color", "font-family", "font-size", "font-style",
  "font-weight", "list-style-type", "margin-left", "padding-left", "text-align",
  "text-decoration", "text-decoration-line", "vertical-align",
]);

// Cheap test for "this entry is markup, not prose".
const FORMATTING_MARKUP_RE =
  /<\s*\/?\s*(a|b|strong|i|em|u|s|strike|del|ins|p|div|span|br|ul|ol|li|h[1-6]|blockquote|small|sup|sub|mark|code|pre|hr)\b[^>]*>/i;

const URL_IN_TEXT_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const readHtmlAttr = (attrs, name) => {
  const match = String(attrs || "").match(
    new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i")
  );
  if (!match) return "";
  return match[2] ?? match[3] ?? match[4] ?? "";
};

// Only http(s)/mailto/in-page targets survive; "javascript:" and friends are
// dropped rather than escaped.
const sanitizeHref = (href) => {
  const value = String(href || "")
    .trim()
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "");
  if (!value) return "";
  if (/^(https?:\/\/|mailto:|\/|#)/i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  return "";
};

const sanitizeInlineStyle = (styleAttr) => {
  const kept = [];
  String(styleAttr || "")
    .split(";")
    .forEach((decl) => {
      const sep = decl.indexOf(":");
      if (sep === -1) return;
      const prop = decl.slice(0, sep).trim().toLowerCase();
      const value = decl.slice(sep + 1).trim();
      if (!prop || !value) return;
      if (!ALLOWED_STYLE_PROPS.has(prop)) return;
      if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) return;
      kept.push(`${prop}: ${value}`);
    });
  return kept.join("; ");
};

const sanitizeRichHtml = (html) =>
  String(html)
    // Drop executable / layout-breaking elements including their content.
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta|noscript|form)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi,
      ""
    )
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(
      /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^"'>])*?)\/?\s*>/g,
      (match, closing, tag, attrs) => {
        const name = tag.toLowerCase();
        if (!ALLOWED_TAGS.has(name)) return "";
        if (name === "br") return "<br />";
        if (closing) return `</${name}>`;
        if (name === "a") {
          const href = sanitizeHref(readHtmlAttr(attrs, "href"));
          return href
            ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="interv-desc-link">`
            : "<a>";
        }
        const style = sanitizeInlineStyle(readHtmlAttr(attrs, "style"));
        return style ? `<${name} style="${escapeHtml(style)}">` : `<${name}>`;
      }
    );

// "https://x.com/y." -> { shown: "https://x.com/y", href: "https://x.com/y" }
// so a sentence-ending full stop is not swallowed into the link target.
const splitTrailingPunctuation = (url) => {
  const trailing = (String(url).match(/[.,;:!?)]+$/) || [""])[0];
  const shown = trailing ? String(url).slice(0, url.length - trailing.length) : String(url);
  return { shown, trailing, href: /^www\./i.test(shown) ? `https://${shown}` : shown };
};

const anchorProps = `target="_blank" rel="noopener noreferrer" class="interv-desc-link"`;

// Plain prose: keep the author's line breaks and turn any bare URL into a
// link (the backend appends citation links straight into the description).
const renderPlainTextWithLinks = (text) => {
  const nodes = [];
  let lastIndex = 0;
  text.replace(URL_IN_TEXT_RE, (match, _group, offset) => {
    nodes.push(text.slice(lastIndex, offset));
    const { shown, trailing, href } = splitTrailingPunctuation(match);
    if (shown) {
      nodes.push(
        <a key={`link-${offset}`} className="interv-desc-link" href={href} target="_blank" rel="noopener noreferrer">
          {shown}
        </a>
      );
    }
    if (trailing) nodes.push(trailing);
    lastIndex = offset + match.length;
    return match;
  });
  nodes.push(text.slice(lastIndex));
  return nodes;
};

// Markup can still carry a bare citation URL as text (the backend appends it
// to the description). Walk the text between tags so already-linked
// <a>...</a> blocks are left untouched.
const linkifyBareUrlsInHtml = (html) =>
  html.replace(/(<a\b[^>]*>[\s\S]*?<\/a>)|([^<]+)/gi, (match, anchor, text) => {
    if (anchor || !text) return match;
    return text.replace(URL_IN_TEXT_RE, (url) => {
      const { shown, trailing, href } = splitTrailingPunctuation(url);
      if (!shown) return url;
      return `<a href="${escapeHtml(href)}" ${anchorProps}>${escapeHtml(shown)}</a>${trailing}`;
    });
  });

// Renders one intervention_description entry (or the whole string).
const renderDescriptionText = (desc, index, style, className) => {
  const text = desc == null ? "" : String(desc).trim();
  if (!text) return null;

  // A <div> (never a <p>) so block level markup coming from the editor
  // (<p>, <ul>, <div>) does not get auto-closed by the HTML parser.
  if (FORMATTING_MARKUP_RE.test(text)) {
    return (
      <div
        key={index}
        className={`interv-desc-rich ${className || ""}`.trim()}
        style={style}
        dangerouslySetInnerHTML={{ __html: linkifyBareUrlsInHtml(sanitizeRichHtml(text)) }}
      />
    );
  }

  return (
    <div
      key={index}
      className={className}
      style={{ whiteSpace: "pre-line", ...(style || {}) }}
    >
      {renderPlainTextWithLinks(text)}
    </div>
  );
};

export default function GoalsetDownloadpage() {

    const normalizeDomain = (d) => String(d || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]/g, "");

    const navigate = useNavigate();
    const [interventionsData, setInterventionsData] = useState(null);
    const [loadingInterventions, setLoadingInterventions] = useState(false);
    // activeGoalTab now stores a goal_id (from the `goals` array), not a domain/skill string
    const [activeGoalTab, setActiveGoalTab] = useState(null);
    const [activeDomainIndex, setActiveDomainIndex] = useState(0);
    const [accordionOpen, setAccordionOpen] = useState({});
    const [activeSheet, setActiveSheet] = useState(null);
    const [activeIntTitle, setActiveIntTitle] = useState(null);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const location = useLocation();
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [toast, setToast] = useState(null);

    const summaryRef = useRef(null);
    const scrollContentRef = useRef(null);
    const token = localStorage.getItem("token");
    const [activeCardIndex, setActiveCardIndex] = useState(null);
    const handlePrevCard = () => {
        setActiveCardIndex((prev) =>
            prev === null
                ? prev
                : (prev - 1 + FOUNDATIONAL_CARDS.length) % FOUNDATIONAL_CARDS.length
        );
    };

    const handleNextCard = () => {
        setActiveCardIndex((prev) =>
            prev === null ? prev : (prev + 1) % FOUNDATIONAL_CARDS.length
        );
    };
    const FOUNDATIONAL_CARDS = [
        {
            title: "Environmental Enrichment",
            image: PEE,
            description:
                "Environmental enrichment is the act of adapting or enhancing an infant's environment to support their learning and development. Environmental enrichment may target cognitive, motor, sensory and social development, and always aims to support neuroplasticity, skill acquisition and goal attainment through everyday activities and routines.",

            keyComponents: [
                {
                    title: "Cognitive enrichment",
                    text: "Provide opportunities for problem solving, learning, attention and exploration."
                },
                {
                    title: "Motor enrichment",
                    text: "Adapt toys, objects and environments to encourage active movement, repetition, practice and skill development."
                },
                {
                    title: "Social enrichment",
                    text: "Provide opportunities for positive social interactions, empathic responses to the infant's expressed needs, and emotional connections with caregivers, family members and peers."
                },
                {
                    title: "Sensory enrichment",
                    text: "Enhance or adapt environments to support visual, hearing and tactile needs while avoiding sensory overload."
                },
                {
                    title: "Optimising learning state",
                    text: "Address factors that influence readiness to learn, such as sleep, pain, fatigue, nutrition and emotional wellbeing."
                }
            ],

            footer: "For more information see CPAdvance Environmental Enrichment."
        },

        {
            title: "Home Programs",
            image: PHP,
            description:
                "Home programs are therapeutic activities that are completed by an infant and their caregiver/s within the home (or other) environment to achieve specific goals. Effective home programs are family-centred, based on caregiver-led goals, and help caregivers to build confidence and skills to support their infant's development in everyday life. Home programs are essential for achieving dosage of therapy.",

            keyComponents: [
                {
                    title: "Collaborative partnerships",
                    text: "Provide caregivers with specific guidance.php to target caregiver-led goals."
                },
                {
                    title: "Context-specific programs",
                    text: "Use the infant's own toys and everyday objects while adapting tasks to suit family routines."
                },
                {
                    title: "Written and pictorial materials",
                    text: "Use photos and videos from the family's environment as reminders for practice."
                },
                {
                    title: "Regular support and feedback",
                    text: "Help implementation and maintain caregiver motivation."
                },
                {
                    title: "Ongoing evaluation",
                    text: "Review progress and goal attainment regularly."
                }
            ],

            footer: "For more information see CPAdvance Home Programs."
        },

        {
            title: "Supporting Caregivers",
            image: PSC,
            description:
                "Supporting caregivers is a family-centred approach that recognises caregivers play the most important role in their infant's development. Therapists support caregivers by building strong relationships, responding to their emotional and practical needs, promoting well-being, and helping them access appropriate supports.",

            keyComponents: [
                {
                    title: "Be Present",
                    text: "Listen actively, respond with empathy and create a safe, non-judgemental space."
                },
                {
                    title: "Empower",
                    text: "Use education, coaching and strengths-based approaches to build caregiver confidence and capability."
                },
                {
                    title: "Detect",
                    text: "Recognise signs of caregiver stress or distress and respond appropriately."
                },
                {
                    title: "Connect",
                    text: "Link caregivers with supports, resources, peer networks and services when needed."
                },
                {
                    title: "Family-centred practice",
                    text: "Recognise each family as unique and work collaboratively to support whole-family well-being."
                }
            ],

            footer: "For more information see CPAdvance Supporting Caregivers."
        },

        {
            title: "Parent Coaching",
            image: PPC,
            description:
                "Parent coaching is a family-centred approach that empowers caregivers as the experts in their child's journey, building on their strengths and equipping them with skills to navigate challenges and achieve meaningful goals. Through a collaborative partnership, therapists and caregivers share knowledge, problem-solve together, and identify strategies that can be embedded into everyday routines and activities.",

            keyComponents: [
                {
                    title: "Joint planning",
                    text: "Real-world problem solving to integrate therapy activities into everyday life."
                },
                {
                    title: "Observation",
                    text: "Observe caregivers carrying out activities in natural environments."
                },
                {
                    title: "Action and practice",
                    text: "Support infants and caregivers to practise goals within and between therapy sessions."
                },
                {
                    title: "Reflection and problem-solving",
                    text: "Overcome challenges, build caregiver capacity and guide therapist support."
                },
                {
                    title: "Feedback",
                    text: "Provide informative, affirmative, evaluative and directive feedback tailored to caregiver needs."
                }
            ],

            footer: "For more information see CPAdvance Coaching."
        },

        {
            title: "Play",
            image: PP,
            description:
                "Play is an essential part of childhood that supports learning, development and participation. Infants learn best when they are engaged, motivated and having fun. In therapy, play should be child-led and integrated into intervention activities to create meaningful opportunities for practice, problem-solving and skill development. Understanding how to use play with purpose is fundamental to effective interventions.",

            keyComponents: [
                {
                    title: "Child-led experimentation",
                    text: "Encourage active problem solving."
                },
                {
                    title: "Child preferences",
                    text: "Follow the infant's interests and use activities and toys they enjoy."
                },
                {
                    title: "Contingency based learning",
                    text: "Associating certain behaviours with outcomes."
                },
                {
                    title: "Affordances",
                    text: "Identify goal limiting factors to allow for adaptation."
                },
                {
                    title: "Play across contexts/environments",
                    text: "Support participation across contexts."
                },
                {
                    title: "Responsive relationships",
                    text: "Support responsive interactions between infant and caregivers."
                }
            ],

            footer: "For more information see CPAdvance Play."
        },

        {
            title: "Equipment",
            image: PEQ,
            description:
                "The appropriate use of equipment for infants with cerebral palsy: The first three years are a period of rapid motor development, where harnessing neuroplasticity and natural recovery is paramount. Selective motor control and weakness are responsive to rehabilitation when targeted with active motor learning interventions (Morganet al., 2021). During this critical period, clinicians should carefully consider the role of equipment, balancing opportunities for active motor development. Thoughtful clinical reasoning is required to ensure equipment enhances, rather than limits, learningopportunities.",

            keyComponents: [
                {
                    title: "Active motor learning",
                    text: "Prioritise interventions that target selective motor control and weakness through active practice."
                },
                {
                    title: "Thoughtful clinical reasoning",
                    text: "Carefully consider whether equipment supports or limits the infant's opportunities for active movement."
                },
                {
                    title: "Balanced opportunities",
                    text: "Balance equipment use with active motor development during the critical first three years."
                }
            ],

            footer: "For more information see CPAdvance Equipment."
        }
    ];

    // -- 1. Session ID Initialization --------------------------------------------
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlSessionId = urlParams.get("sessionId");

        if (urlSessionId) {
            setCurrentSessionId(urlSessionId);
            return;
        }

        const savedState = localStorage.getItem("assessmentState");
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                if (parsed.currentSessionId) {
                    setCurrentSessionId(parsed.currentSessionId);
                }
            } catch (e) {
                console.error("Error parsing saved state", e);
            }
        }
    }, [location.search]);

    // -- 2. Fetch Data ------------------------------------------------------------
    const fetchInterventions = async () => {
        if (!currentSessionId) return;

        setInterventionsData(null);
        setActiveGoalTab(null);
        setActiveDomainIndex(0);
        setAccordionOpen({});
        setLoadingInterventions(true);

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/get_test_intervention.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        session_id: parseInt(currentSessionId),
                    }),
                }
            );

            const json = await response.json();

            if (json.status && json.data) {
                // A goal revision (/updategoal "Revise Selected Intervention",
                // and every /goalreview Update) is APPENDED as a Goal Review
                // snapshot, so it no longer lives in the planning rows this page
                // used to read. Resolve the newest saved version here, or the
                // download would keep showing the goal as it was first set
                // instead of everything that was just submitted.
                const effective = withNewestGoalVersion(json);
                setInterventionsData(effective);

                // Tabs are now driven by the saved `goals` array (one tab per goal),
                // not by the intervention catalog keys.
                const goalsList = effective.goals || [];

                if (goalsList.length > 0) {
                    const firstGoal = goalsList[0];
                    setActiveGoalTab(firstGoal.goal_id);

                    // Open the first "Suggested Interventions" accordion item that
                    // belongs to this goal's domain (catalog lookup, unchanged logic).
                    const catalogKey = Object.keys(json.data || {}).find((key) =>
                        (json.data[key] || []).some(
                            (item) => item.goal_domain === firstGoal.goal_domain
                        )
                    );
                    const catalogItems = catalogKey ? json.data[catalogKey] || [] : [];
                    if (catalogItems.length > 0 && catalogItems[0].id) {
                        setAccordionOpen({ [`int-${catalogItems[0].id}`]: true });
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching interventions:", error.message);
        } finally {
            setLoadingInterventions(false);
        }
    };

    // Keyed on the session only: re-running this on every render would refetch
    // (and reset the accordion state) on each keystroke elsewhere.
    useEffect(() => {
        if (currentSessionId) {
            fetchInterventions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId]);

    // -- 3. Helper Components & Functions ----------------------------------------

    // Intervention image URLs come back from the API relative to the app folder
    // ("uploads/interventions/x.jpg"); assetUrl() resolves them against
    // API_BASE and passes a full URL through untouched.
    const AccordionItem = ({ title, image, text, id }) => {
        return (
            <div className="interventions-card" id={id}>
                <h3 className="interventions-card-title">{"\u2714"} {title}</h3>
                <div className="interventions-card-body">{text}</div>
                {!isGeneratingPdf && image && (
                    <img src={assetUrl(image)} alt="" className="interventions-card-img" aria-hidden="true" />
                )}
            </div>
        );
    };

    // -- 4. PDF Generation Logic --------------------------------------------------

    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement("script");
            script.src = src;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    };

    const client = interventionsData?.session?.client_id || "client";

    const openAllAccordions = () => {
        const previousState = { ...accordionOpen };
        const newState = {};

        if (interventionsData && interventionsData.data && interventionsData.goals) {
            const { data: interventionsMap, goals, session: sessionInfo } = interventionsData;

            // Open existing goal accordions
            goals.forEach((goal) => {
                const catalogKey = Object.keys(interventionsMap || {}).find((key) =>
                    (interventionsMap[key] || []).some(
                        (item) => item.goal_domain === goal.goal_domain
                    )
                );
                const suggestedInterventions = catalogKey ? interventionsMap[catalogKey] || [] : [];

                suggestedInterventions
                    .filter((item) => item.intervention_title && item.intervention_description)
                    .forEach((intervention, index) => {
                        const id = `int-${goal.goal_id}-${intervention.id || index}`;
                        newState[id] = true;
                    });
            });

            // Open skill tab accordions for all skills
            const rawDomains = sessionInfo?.goal_domains;
            const rawSkills = sessionInfo?.goal_skills;
            const doms = Array.isArray(rawDomains) ? rawDomains : (rawDomains ? [rawDomains] : []);
            const skills = Array.isArray(rawSkills) ? rawSkills : (rawSkills ? [rawSkills] : []);
            doms.forEach((d, i) => {
                const skillKey = skills[i] || d;
                const skillInterventions = interventionsMap?.[skillKey] || [];
                skillInterventions
                    .filter((item) => item.intervention_title && item.intervention_description)
                    .forEach((intervention, index) => {
                        const id = `int-skill-${intervention.id || index}`;
                        newState[id] = true;
                    });
            });
        }

        setAccordionOpen(newState);
        return previousState;
    };

    const captureSummaryAsPdfBlob = async () => {
        if (!summaryRef.current) throw new Error("Summary element not found");
        try {
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
        } catch (err) {
            throw new Error("Failed to load PDF libraries.");
        }

        if (!window.html2canvas || !window.jspdf) {
            throw new Error("PDF libraries are still not available on window object.");
        }

        await new Promise((r) => setTimeout(r, 800));

        const element = summaryRef.current;

        const canvas = await window.html2canvas(element, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
        });

        const { jsPDF } = window.jspdf;

        const margin = 10; // mm padding on all 4 sides
        const a4Width = 210;
        const a4Height = 297;
        const contentWidth = a4Width - margin * 2;
        const contentHeight = a4Height - margin * 2;

        const pdf = new jsPDF("p", "mm", "a4");

        // Slice the canvas into clean pages: breaks fall on blank gaps so no
        // word/line is split, never cut through an image, and honour any
        // element marked with the .pdf-page-break class.
        const pages = paginateCanvasToPdf({
            canvas,
            element,
            pdfWidth: contentWidth,
            pageHeightMm: contentHeight,
            minChunkFraction: 0.1,
        });

        pages.forEach((p, i) => {
            if (i > 0) pdf.addPage();
            pdf.addImage(p.sliceImgData, "JPEG", margin, margin, contentWidth, p.sliceHeightMm);
        });

        return pdf.output("blob");
    };

    const handleDownloadSummary = async () => {
        const confirmed = window.confirm(
            "This will download a PDF summary of the entire assessment including all interventions. Do you want to continue?"
        );
        if (!confirmed) return;

        setIsGeneratingPdf(true);
        const previousState = openAllAccordions();

        try {
            const blob = await captureSummaryAsPdfBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `assessment-summary-${client || "report"}-${new Date()
                .toISOString()
                .slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setToast({ message: "PDF downloaded successfully.", type: "success" });
        } catch (err) {
            console.error("PDF generation failed:", err);
            setToast({ message: "Failed to generate PDF. Please try again.", type: "error" });
        } finally {
            setAccordionOpen(previousState);
            setIsGeneratingPdf(false);
        }
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };
    // -- 5. Render Logic ----------------------------------------------------------

    // Renders the goal tabs + everything specific to whichever goal tab is
    // currently active. Tabs = json.goals (the saved goals), so clicking a
    // tab swaps ALL the fields below (Goal Domain, Status, Result Date,
    // SMART goal, Goal Review Date, Suggested Interventions, the selected
    // intervention/dosage list, and Outcome Measure) to that goal only.
    const renderGoalTabsAndInterventions = () => {
        if (!interventionsData || !interventionsData.session) return null;

        const { session: sessionInfo, data: interventionsMap, goals } = interventionsData;

        const rawDomains = sessionInfo.goal_domains;
        const rawSkills = sessionInfo.goal_skills;
        const goalDomains = Array.isArray(rawDomains) ? rawDomains : (rawDomains ? [rawDomains] : []);
        const goalSkills = Array.isArray(rawSkills) ? rawSkills : (rawSkills ? [rawSkills] : []);
        const seenPairKeys = new Set();
        const skillPairs = goalDomains
            .map((d, i) => {
                const rawSkill = goalSkills[i] || '';
                const skillMatchesDomain = normalizeDomain(rawSkill) === normalizeDomain(d);
                return {
                    goal_domain: d,
                    goal_skill: rawSkill && !skillMatchesDomain ? rawSkill : '',
                };
            })
            .filter((p) => {
                const key = `${normalizeDomain(p.goal_domain)}||${normalizeDomain(p.goal_skill)}`;
                if (seenPairKeys.has(key)) return false;
                seenPairKeys.add(key);
                return true;
            });

        // Interventions actually checked in the "made in collaboration with the
        // family" list, collected across ALL saved goals. Each checked value is
        // matched against the catalog by excel_sheet so the matching
        // title/description/image cards render below. This drives the
        // "Selected Interventions" section (and each goal_domain - goal_skill
        // tab), NOT the whole catalog for the skill.
        const normKey = (s) => String(s || "").toLowerCase().replace(/[\s:.-]+/g, "");
        const checkedSheetValues = [];
        (Array.isArray(goals) ? goals : []).forEach((g) => {
            (g.interventions || []).forEach((iv) => {
                const raw = Array.isArray(iv.selected_intervention)
                    ? iv.selected_intervention
                    : typeof iv.selected_intervention === "string" && iv.selected_intervention.trim()
                        ? iv.selected_intervention.split(",").map((s) => s.trim()).filter(Boolean)
                        : [];
                raw.forEach((t) => {
                    if (t && !checkedSheetValues.some((x) => normKey(x) === normKey(t))) checkedSheetValues.push(t);
                });
            });
        });
        const allCatalogItems = Object.values(interventionsMap || {}).flat() || [];
        const selectedCatalogItems = checkedSheetValues.flatMap((val) =>
            allCatalogItems.filter(
                (item) => item.excel_sheet && normKey(item.excel_sheet) === normKey(val)
            )
        );
        const itemMatchesPair = (item, pair) => {
            const dom = normKey(pair.goal_domain);
            const skill = normKey(pair.goal_skill);
            const iDom = normKey(item.goal_domain || "");
            const iSkill = normKey(item.goal_skill && item.goal_skill !== "null" ? item.goal_skill : "");
            const iSheet = normKey(item.excel_sheet || "");
            if (skill && (iSkill === skill || iDom === skill || iSheet === skill || iSheet.indexOf(skill) !== -1)) return true;
            if (dom && (iDom === dom || iSkill === dom || iSheet === dom || iSheet.indexOf(dom) !== -1)) return true;
            return !dom && !skill;
        };

        // Which tabs render as "active" is decided by the excel_sheet (selected
        // intervention) data, NOT by a single clicked index. A tab whose
        // domain/skill pair matches at least one selected item is active, so a
        // session with interventions checked under two different skills lights
        // up BOTH tabs, and a session with data under only one skill lights up
        // just that one. This Set holds every data-backed tab index.
        const dataBackedTabIndices = new Set();
        skillPairs.forEach((pair, idx) => {
            if (selectedCatalogItems.some((item) => itemMatchesPair(item, pair))) {
                dataBackedTabIndices.add(idx);
            }
        });

        // The set of ACTIVE (data-backed) tabs decides what appears in the left
        // Navigation menu and the right scroll area. When two tabs are active,
        // the excel_sheet data of BOTH tabs is combined and shown together, so
        // the menu lists every sheet from both tabs and the right side renders
        // a section per sheet. If no tab happens to be data-backed, fall back
        // to all pairs so the page is never empty.
        const activePairs = skillPairs.filter((_, idx) => dataBackedTabIndices.has(idx));
        const pairsToShow = activePairs.length > 0 ? activePairs : skillPairs;
        const tabFilteredCatalogItems = selectedCatalogItems.filter((item) =>
            pairsToShow.some((pair) => itemMatchesPair(item, pair))
        );

        // The API may return one row per intervention, so the same goal can
        // appear more than once. Dedupe so each goal (and its Goal Domain
        // box) is rendered only once in the PDF.
        const seenGoalIds = new Set();
        const uniqueGoals = (Array.isArray(goals) ? goals : []).filter((g) => {
            const id = g && g.goal_id;
            if (id == null || seenGoalIds.has(id)) return false;
            seenGoalIds.add(id);
            return true;
        });

        const selectedGoal =
            uniqueGoals?.find((g) => g.goal_id === activeGoalTab) || uniqueGoals?.[0] || null;

        const renderGoalContent = (goal) => {
            const selectedInterventions = goal.interventions || [];
            // "Domain - Skill" tags, de-duplicated: a goal with no skill of its
            // own shows the domain alone rather than "Vision-Vision". The tabs
            // on this page step through these, so the heading has to match what
            // the Goal Domain field shows everywhere else.
            const uniqueGoalDomains = buildDomainTagsFromGoal(goal);
            const goalDomainDisplay = uniqueGoalDomains[activeDomainIndex] || uniqueGoalDomains.join(", ");

            return (
                <div
                    key={goal.goal_id}
                    className="pdf-page-break"
                    style={isGeneratingPdf ? { marginBottom: "30px", paddingBottom: "20px" } : undefined}
                >
                    {isGeneratingPdf && (
                        <h3 style={{ color: "#247A33", marginBottom: "10px" }}>
                            {goalDomainDisplay}
                        </h3>
                    )}

                    <div style={{ display: "flex", gap: "16px", marginBottom: "25px", alignItems: "flex-start", width: "100%" }}>
                        <div className="ug-field" style={{ flex: "1 1 62%", display: "flex", flexDirection: "column" }}>
                            <label style={{ fontSize: "14px", color: "#247A33", fontWeight: "bold" }}>
                                Write in the infant's SMART goal related to the goal domains selected.
                            </label>
                            <textarea
                                key={`smart-${goal.goal_id}`}
                                style={{ width: "100%", height: "42px", border: "1px solid #b0bec5", borderRadius: "8px", padding: "8px 12px", fontSize: "14px", color: "#333", resize: "none", background: "#f9f9f9", boxSizing: "border-box", fontWeight: 'bold' }}
                                readOnly
                                defaultValue={goal.smart_goal || ""}
                            />
                        </div>
                        <div className="ug-field" style={{ flex: "1 1 38%", display: "flex", flexDirection: "column" }}>
                            <label style={{ fontSize: "14px", color: "#247A33", fontWeight: "bold" }}>
                                Goal Target Date
                            </label>
                            <div style={{ border: "1px solid #b0bec5", borderRadius: "8px", padding: "8px 12px", fontSize: "14px", background: "#f9f9f9", color: "#333", fontWeight: 'bold', height: "42px", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                                {formatDateDisplay(goal.target_date || "-")}
                            </div>
                        </div>
                    </div>
                    {/* -- Selected interventions (checkbox list) -- */}
                    <div className="ug-field full-width" style={{ width: "100%", marginBottom: "25px" }}>
                        <label style={{ fontSize: "14px", color: "#247A33", fontWeight: "bold" }}>
                            Select the intervention/s choices that have been made in collaboration with the family
                        </label>

                        {selectedInterventions.length === 0 ? (
                            <div style={{ border: "1px solid #b0bec5", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", background: "#f9f9f9", color: "#666" }}>
                                No interventions selected for this goal.
                            </div>
                        ) : (
                            selectedInterventions.map((iv, ivIdx) => (
                                <div className="ug-intervention-block" key={iv.intervention_id || ivIdx}>
                                    <div className="grv-selection-list">
                                        {(() => {
                                            const selections = Array.isArray(iv.selected_intervention)
                                                ? iv.selected_intervention
                                                : typeof iv.selected_intervention === "string" && iv.selected_intervention.trim()
                                                    ? iv.selected_intervention.split(",").map(s => s.trim()).filter(Boolean)
                                                    : [];
                                            if (selections.length === 0) {
                                                return (
                                                    <div style={{ padding: "10px 14px", fontSize: 14, color: "#999" }}>
                                                        N/A
                                                    </div>
                                                );
                                            }
                                            return selections.map((item, i2) => (
                                                <label
                                                    className="grv-selection-option"
                                                    key={i2}
                                                    style={{
                                                        color: "#323C43",
                                                        fontWeight: 700,
                                                        background: "#EBF7E9",
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked
                                                        readOnly
                                                        aria-label={item}
                                                    />
                                                    <span style={{ color: "#323C43", fontWeight: 700 }}>{item}</span>
                                                </label>
                                            ));
                                        })()}
                                    </div>

                                    <div className="grv-sub-title">Dosage of Home Practice to Support this Goal</div>
                                    <div className="grv-counters-3col">
                                        <div className="grv-counter-field">
                                            <label>Number of minutes per day</label>
                                            <div className="grv-counter1">

                                                <input className="grv-counter-input1" style={{width:'60px'}} value={iv.dosage_minutes_per_day ?? 0} readOnly />

                                            </div>
                                        </div>
                                        <div className="grv-counter-field">
                                            <label>Number of weeks</label>
                                            <div className="grv-counter1">
                                                <input className="grv-counter-input1" style={{width:'60px'}} value={iv.dosage_weeks ?? 0} readOnly />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grv-sub-title">Therapists Sessions to Support this Goal</div>
                                    <div className="grv-counters-1col">
                                        <div className="grv-counter-field">
                                            <label>Number of Sessions</label>
                                            <div className="grv-counter1">
                                                <input className="grv-counter-input1" style={{width:'60px'}} value={iv.therapist_sessions ?? 0} readOnly />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* -- Outcome Measure -- */}
                    <div className="ug-field full-width" style={{ marginBottom: "25px" }}>
                        <label htmlFor={`ug-outcome-${goal.goal_id}`}>
                            What outcome measure are you using?
                        </label>
                        <textarea
                            key={`outcome-${goal.goal_id}`}
                            id={`ug-outcome-${goal.goal_id}`}
                            readOnly
                            style={{ width: "100%", border: "1px solid #b0bec5", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", color: "#333", resize: "none", background: "#f9f9f9", height: "42px", boxSizing: "border-box", fontWeight: 'bold' }}
                            defaultValue={goal.outcome_measure || ""}
                            rows={1}
                        />
                    </div>
                </div>
            );
        };

        return (
            <>

                {/* -- Existing Goals -- */}
                {!goals || goals.length === 0 ? (
                    <p>No goals have been saved for this session.</p>
                ) : (
                    <>
                        {/* Goal content: single goal normally, ALL goals when generating PDF */}
                        {isGeneratingPdf
                            ? uniqueGoals.map((g) => renderGoalContent(g))
                            : selectedGoal
                                ? renderGoalContent(selectedGoal)
                                : null
                        }
                    </>
                )}

                {/* -- Suggested Interventions -- */}
                {isGeneratingPdf ? (
                    skillPairs.map((pair, idx) => {
                        const skillInterventions = pair.goal_skill ? (interventionsMap?.[pair.goal_skill] || []) : (interventionsMap?.[pair.goal_domain] || []);
                        return (
                            <div className="suggested-section1 pdf-page-break" key={idx}>
                                <h2 className="suggested-title1">{pair.goal_skill ? `${pair.goal_domain} - ${pair.goal_skill}` : pair.goal_domain}</h2>
                                {skillInterventions.filter((item) => item.intervention_title && item.intervention_description).length > 0 ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                        {skillInterventions
                                            .filter((item) => item.intervention_title && item.intervention_description)
                                            .map((intervention, index) => (
                                                <AccordionItem
                                                    key={intervention.id || index}
                                                    title={intervention.intervention_title}
                                                    image={intervention.image_url}
                                                    excelSheet={intervention.excel_sheet}
                                                    text={
                                                        Array.isArray(intervention.intervention_description) ? (
                                                            <div>
                                                                {intervention.intervention_description.map((desc, i) =>
                                                                    renderDescriptionText(desc, i, { marginBottom: "15px", lineHeight: "1.6", textAlign: "justify", wordBreak: "break-word", overflowWrap: "break-word" })
                                                                )}
                                                            </div>
                                                        ) : (
                                                            renderDescriptionText(intervention.intervention_description, 0, { lineHeight: "1.7", textAlign: "justify", wordBreak: "break-word", overflowWrap: "break-word" })
                                                        )
                                                    }
                                                    id={`int-skill-${intervention.id || index}`}
                                                />
                                            ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: "center", padding: "30px 20px", background: "#f8f9fa", borderRadius: "8px", marginTop: "15px" }}>
                                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 500, color: "#666" }}>No Selected Interventions found for this skill</p>
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="suggested-section1">
                        <h2 className="suggested-title1">Selected Interventions</h2>

                        {loadingInterventions ? (
                            <p style={{ textAlign: "center", padding: "20px" }}>Loading interventions...</p>
                        ) : (() => {
                            const validInterventions = (tabFilteredCatalogItems.length > 0
                                ? tabFilteredCatalogItems
                                : selectedCatalogItems).filter(
                                (item) => item.intervention_title && item.intervention_description
                            );
                            if (validInterventions.length === 0) {
                                return (
                                    <div style={{ textAlign: "center", padding: "30px 20px", background: "#f8f9fa", borderRadius: "8px", marginTop: "15px" }}>
                                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 500, color: "#666" }}>
                                            No Selected Interventions found for the selected skill
                                        </p>
                                    </div>
                                );
                            }
                            const grouped = {};
                            validInterventions.forEach((item) => {
                                const sheet = item.excel_sheet || "General";
                                if (!grouped[sheet]) grouped[sheet] = [];
                                grouped[sheet].push(item);
                            });
                            const sheetNames = Object.keys(grouped);

                            return (
                                <div style={{ display: "flex", gap: "0", marginTop: "15px", minHeight: "400px" }}>
                                    <button
                                        className="interventions-mobile-menu-btn"
                                        onClick={() => setMobileSidebarOpen(true)}
                                        aria-label="Open navigation sidebar"
                                    >
                                        &#9776;
                                    </button>
                                    {mobileSidebarOpen && (
                                        <div className="interventions-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
                                    )}
                                    <nav className={`interventions-sidebar ${mobileSidebarOpen ? "mobile-open" : ""}`} aria-label="Intervention sections navigation">
                                        <div className="interventions-sidebar-header">
                                            <span>Navigation</span>
                                            <button className="interventions-sidebar-close" onClick={() => setMobileSidebarOpen(false)} aria-label="Close navigation sidebar">&#10005;</button>
                                        </div>
                                        {sheetNames.map((sheet, sheetIdx) => {
                                            const items = grouped[sheet] || [];
                                            const sheetOpen = activeSheet === sheet || (activeSheet === null && sheetIdx === 0);
                                            return (
                                                <div key={sheet}>
                                                    <div
                                                        className={`interventions-sidebar-sheet ${sheetOpen ? "active" : ""}`}
                                                        onClick={() => {
                                                            setActiveSheet(sheetOpen ? null : sheet);
                                                            if (!sheetOpen && items.length > 0) {
                                                                const targetId = `intv-${items[0].id}`;
                                                                const el = document.getElementById(targetId);
                                                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                                            }
                                                        }}
                                                    >
                                                        <span>{sheet}</span>
                                                        <span style={{ fontSize: "11px" }}>{sheetOpen ? "\u25BC" : "\u25B6"}</span>
                                                    </div>
                                                    {sheetOpen && items.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className={`interventions-sidebar-title ${activeIntTitle === item.intervention_title ? "active" : ""}`}
                                                            onClick={() => {
                                                                setActiveIntTitle(item.intervention_title);
                                                                setMobileSidebarOpen(false);
                                                                const targetId = `intv-${item.id}`;
                                                                const el = document.getElementById(targetId);
                                                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                                            }}
                                                            title={item.intervention_title}
                                                        >
                                                            <span className="interventions-sidebar-dot">&#8226;</span>
                                                            <span className="interventions-sidebar-title-text">{item.intervention_title}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </nav>
                                    <div className="interventions-scroll-content" ref={scrollContentRef}>
                                        {sheetNames.map((sheet, sheetIdx) => {
                                            const items = grouped[sheet] || [];
                                            return (
                                                <div key={sheet} className="interventions-sheet-section">
                                                    <div style={{ display: "flex", alignItems: "center", gap: "18px", marginBottom: "10px",marginLeft:"10px", background: "#e8f5e9", borderLeft: "4px solid #247A33", borderRadius: "4px", padding: "10px 14px" }}>
                                                        <h4 className="interventions-sheet-heading" style={{ margin: "0", background: "transparent", borderLeft: "none", padding: "0", whiteSpace: "nowrap" }}>{sheet}</h4>
                                                        {sheetIdx === 0 && (
                                                            <div className="goal-toggle3" style={{ justifyContent: "flex-start", padding: "0" }}>
                                                                {skillPairs.map((pair, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className={`toggle-btn3 ${dataBackedTabIndices.has(idx) ? "active" : ""}`}

                                                                        onClick={() => {
                                                                            setAccordionOpen({});
                                                                            setActiveSheet(null);
                                                                            setActiveIntTitle(null);
                                                                            setMobileSidebarOpen(false);
                                                                            // Show the combined data of every active
                                                                            // tab from the top of the right pane.
                                                                            const pane = scrollContentRef.current;
                                                                            if (pane) pane.scrollTo({ top: 0, behavior: "smooth" });
                                                                        }}
style={{
                                                                            padding: "8px 12px",
                                                                            boxSizing: "border-box",
                                                                            background: "#fff",
                                                                            color: "#247A33",
                                                                            border: "1px solid #247A33",
                                                                            borderRadius: "50px",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {pair.goal_skill ? `${pair.goal_domain} - ${pair.goal_skill}` : pair.goal_domain}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {items.map((item) => (
                                                        <div key={item.id} id={`intv-${item.id}`} className="interventions-card">
                                                            <h3 className="interventions-card-title">{"\u2714"} {item.intervention_title}</h3>
                                                            <div className="interventions-card-body">
                                                                {Array.isArray(item.intervention_description) ? (
                                                                    item.intervention_description.map((desc, i) =>
                                                                        renderDescriptionText(desc, i, undefined, "interventions-card-desc")
                                                                    )
                                                                ) : (
                                                                    renderDescriptionText(item.intervention_description, 0, undefined, "interventions-card-desc")
                                                                )}
                                                            </div>
                                                            {item.image_url && (
                                                                <img src={assetUrl(item.image_url)} alt="" className="interventions-card-img" aria-hidden="true" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

            </>
        );
    };

    // -- 6. Main JSX --------------------------------------------------------------

    return (
        <>
            <div className="gd-page">
                <div className="gd-header-row">
                    <div
                        className="back-btn2"
                        onClick={() => navigate(-1)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(-1); } }}
                        role="button"
                        tabIndex={0}
                        aria-label="Go to previous page"
                        style={{ cursor: "pointer" }}
                    >
                        <FaArrowLeft className="back-icon" />
                        Previous page
                    </div>
                    {/* -- Page Header -- */}
                    <div className="common-page-header gd-header-inline">
                        <h1 className="common-page-title">Intervention Planning</h1>
                        <p className="common-page-subtitle">Download your intervention planning here</p>
                    </div>
                </div>


                {/* -- Summary Content (for PDF capture) -- */}
                <div className="summary-container111" ref={summaryRef}>
                    <div className="sum-cont1">

                        {/* -- Medical Info Card -- */}
                        <div className="summ-container11">
                            <div className="sum-cont11">

                                {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                                <div className="summ-client-row">
                                    {interventionsData?.session?.client_name || " "}'s Goals
                                </div>

                                {/* Section 1 - Row 2: Metadata pills */}
                                <div className="summ-meta-row" >
                                    <span className="summ-meta-pill summ-meta-plain">
                                        <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                                        <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                            {(() => {
                                                const code = String(interventionsData?.session?.client_code || "");
                                                const digits = code.replace(/\D/g, "").slice(0, 8);
                                                if (digits.length === 8) {
                                                    const dd = digits.slice(0, 2);
                                                    const mm = digits.slice(2, 4);
                                                    const yyyy = digits.slice(4, 8);
                                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                    const mIdx = parseInt(mm, 10) - 1;
                                                    return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                                }
                                                return "";
                                            })()}
                                        </span>
                                    </span>
                                    <span className="summ-meta-pill summ-meta-green">
                                        <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                                        <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                                    </span>
                                    <span className="summ-meta-pill summ-meta-green">
                                        <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                                        <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ""}</span>
                                    </span>
                                </div>

                                {/* Section 1 - Row 3: Section title */}
                                <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                                {/* Section 1 - Row 4: Topography + Severity */}
                                <div className="summ-findings-block">
                                    <div className="summ-finding-row">
                                        <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                                        <strong className="summ-finding-label">Topography:&nbsp;</strong>
                                        <span className="summ-value-pill">{interventionsData?.session?.topography || ""}</span>
                                    </div>
                                    <div className="summ-finding-row">
                                        <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                                        <strong className="summ-finding-label">Severity:&nbsp;</strong>
                                        <span className="summ-value-pill">{interventionsData?.session?.severity || ""}</span>
                                    </div>
                                </div>

                                {/* Section 1 - Row 5: Early indicators of comorbidities */}
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                                    <span className="summ-value-pill">
                                        {interventionsData?.session?.comorbidities?.length
                                            ? interventionsData.session.comorbidities.map(item => item.type).join(", ")
                                            : ""}
                                    </span>
                                </div>
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                                    <span className="summ-value-pill">
                                        {(() => {
                                            const factors = interventionsData?.session?.goal_limiting_factors || [];
                                            return factors.join(", ");
                                        })()}
                                    </span>
                                </div>

                                {/* Section 1 - Row 6: Goals */}
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Goals:&nbsp;</strong>
                                    <span className="summ-value-pill">
                                        {(() => {
                                            const map = interventionsData?.data || {};
                                            const tabs = Object.keys(map).map((key) => {
                                                const items = map[key] || [];
                                                const first = items[0] || {};
                                                const domain = first.goal_domain || key || "";
                                                const skill = first.goal_skill || "";
                                                return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
                                            });
                                            return [...new Set(tabs)].join(", ");
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {renderGoalTabsAndInterventions()}

                        {/* Info Box: mCIMT vs Bimanual */}
                        <div className="ug-info-box" style={{marginTop:'10px'}}>
                            <h4 className="ug-info-title">Foundational Interventions</h4>
                            <p className="ug-info-text">
                                Foundational interventions should be embedded in conjunction with specific interventions when working with infants with cerebral palsy and their caregivers: home programs, play, coaching, supporting caregivers and environmental enrichment.
                            </p>
                        </div>

                        {/* 5 Cards Grid - click a card to preview it in the popup.
                           During PDF generation we instead render each card's FULL
                           content (image + description + key components + footer)
                           inline, since the popup/modal content never mounts into
                           the DOM otherwise and so never gets captured by html2canvas. */}
                        {isGeneratingPdf ? (
                            <div className="ug-cards-pdf-content">
                                {FOUNDATIONAL_CARDS.map((card) => (
                                    <div
                                        className="ug-card-pdf-full pdf-page-break"
                                        key={card.title}
                                        style={{
                                            display: "flex",
                                            gap: "20px",
                                            alignItems: "flex-start",
                                            marginBottom: "24px",
                                            paddingBottom: "20px",
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ color: "var(--cpa-dark-gray, #323C43)", margin: "0 0 8px 0", fontSize: "14px", fontWeight: 600 }}>
                                                {card.title}
                                            </h4>
                                            <p
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1.7",
                                                    textAlign: "justify",
                                                    color: "var(--cpa-dark-gray, #323C43)",
                                                    margin: "0 0 10px 0",
                                                }}
                                            >
                                                {card.description}
                                            </p>
                                            <div className="key-components-title" style={{ fontSize: "14px", fontWeight: 600, color: "var(--cpa-dark-gray, #323C43)", marginTop: "5px" }}>
                                                Key components
                                            </div>
                                            <ul className="key-components-list">
                                                {card.keyComponents.map((item, i) => (
                                                    <li key={i}>
                                                        <strong>{item.title}:</strong> {item.text}
                                                    </li>
                                                ))}
                                            </ul>
                                            {card.footer && (
                                                <p
                                                    className="footer-text"
                                                    style={{ fontSize: "14px", fontStyle: "italic", color: "var(--cpa-dark-gray, #323C43)" }}
                                                >
                                                    {card.footer}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="ug-cards-grid">
                                {FOUNDATIONAL_CARDS.map((card, cardIdx) => (
                                    <div
                                        className="ug-card"
                                        key={card.title}
                                        onClick={() => setActiveCardIndex(cardIdx)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveCardIndex(cardIdx); } }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`View details for ${card.title}`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <div className="ug-card-image">
                                            <img src={card.image} alt={card.title} />
                                        </div>
                                        <div className="ug-card-content">
                                            <h5>{card.title}</h5>

                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

              

                {/* -- Footer Buttons -- */}
                <div className="ug-footer">
                    <button
                        style={{ backgroundColor: '#247A33', color: 'white', marginBottom: '20px' }}
                        className="ug-download-btn"
                        onClick={handleDownloadSummary}
                        disabled={isGeneratingPdf}
                        aria-label="Download summary PDF"
                    >
                        Download <FaDownload className="btn-icon" aria-hidden="true" />
                    </button>

                    <button
                        style={{ marginBottom: '20px' }}
                        className="ug-download-btn"
                        onClick={() => navigate("/Home")}
                        disabled={isGeneratingPdf}
                        aria-label="Exit to home"
                    >
                        Exit
                    </button>

                </div>
            </div>

            {/* -- PDF Loading Overlay -- */}
            {isGeneratingPdf && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(255,255,255,0.85)",
                        zIndex: 10000,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "16px",
                    }}
                >
                    <div
                        style={{
                            width: "48px",
                            height: "48px",
                            border: "5px solid #e0e0e0",
                            borderTop: "5px solid #2b8c29",
                            borderRadius: "50%",
                            animation: "spin 0.9s linear infinite",
                        }}
                    />
                    <p style={{ fontSize: "18px", fontWeight: "bold", color: "#2b8c29" }}>
                        Generating PDF, please wait...
                    </p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
            {/* Foundational intervention card popup - matches the provided reference design.
               Not remounted via key anymore (the modal now stays mounted while cycling
               through cards via the arrows), the description just resets when the
               underlying card index changes. */}
            {activeCardIndex !== null && (
                <FoundationalCardModal
                    card={FOUNDATIONAL_CARDS[activeCardIndex]}
                    cardIndex={activeCardIndex}
                    total={FOUNDATIONAL_CARDS.length}
                    onClose={() => setActiveCardIndex(null)}
                    onPrev={handlePrevCard}
                    onNext={handleNextCard}
                />
            )}

            {/* Styles for the new multi-select dropdown and the card popup modal.
               Added here (rather than the existing CSS files) so nothing already
               in review.css / UpdateGoal.css is touched. */}
            <style>{`
                /* Page wrapper: centred, equal left/right gap, responsive
                   padding so content never touches the screen edges. */
                .gd-page {
                    width: 100%;
                    box-sizing: border-box;
                    overflow-x: hidden;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 20px;
                }
                @media (max-width: 1024px) {
                    .gd-page { padding: 16px; }
                }
                @media (max-width: 768px) {
                    .gd-page { padding: 14px; }
                }
                @media (max-width: 480px) {
                    .gd-page { padding: 10px; }
                }

                /* Page header: Previous page button on the left and the
                   title/subtitle centred, matching UpdateGoal/Goal Review. */
                .gd-header-row {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    padding: 0;
                    margin-bottom: 40px;
                }
                .gd-header-row .back-btn2 {
                    position: absolute;
                    left: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    margin: 0;
                    flex-shrink: 0;
                }
                .gd-header-inline {
                    text-align: center;
                    margin: 0;
                    padding: 0;
                }
                @media (max-width: 768px) {
                    .gd-header-row {
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 12px;
                    }
                    .gd-header-row .back-btn2 {
                        position: static;
                        transform: none;
                    }
                }

                /* Uniform 25px spacing rules for the downloaded summary.
                   Kept local so review.css / UpdateGoal.css are not touched. */
                .summ-container11 {
                    width: 100%;
                    margin-top: 0px;
                    color: #333;
                    padding: 0 0 15px 0;
                }
                .sum-cont11 {
                    flex: 1;
                    padding: 0px 20px 10px 0px;
                    width: 100%;
                }
                .summ-client-row {
                    text-align: center;
                    font-size: 18px;
                    font-weight: 700;
                    color: #247a33;
                    margin-bottom: 14px;
                }
                .summ-meta-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    justify-content: flex-start;
                    align-items: center;
                    margin-bottom: 18px;
                    text-align: left;
                }
                .summ-meta-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #247a33;
                }
                .summ-meta-value {
                    font-size: 14px;
                    font-weight: 500;
                    color: #58b947;
                }
                .summ-meta-pill {
                    display: inline-flex;
                    align-items: center;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 50px;
                    padding: 5px 12px;
                }
                .summ-meta-plain {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                .summ-meta-green .summ-meta-label {
                    color: #58b947;
                }
                .summ-section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #323C43;
                    margin: 6px 0 14px 0;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-findings-block {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .summ-finding-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                    padding: 8px 12px;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-finding-label {
                    color: #323c43;
                    font-weight: 700;
                    font-size: 15px;
                    white-space: pre-wrap;
                }
                .summ-value-pill {
                    display: inline-block;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    padding: 4px 10px;
                    font-size: 14px;
                    color: #323c43;
                    font-weight: 500;
                }
                .summ-finding-row .summ-value-pill {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                /* Rich text coming from the intervention_description editor.
                   Declared here (not in the shared CSS) so the on-screen view and
                   the html2canvas snapshot of it lay the markup out identically. */
                .interv-desc-rich p,
                .interv-desc-rich div {
                    margin: 0 0 6px 0;
                }
                .interv-desc-rich p:last-child,
                .interv-desc-rich div:last-child,
                .interv-desc-rich ul:last-child,
                .interv-desc-rich ol:last-child {
                    margin-bottom: 0;
                }
                .interv-desc-rich ul,
                .interv-desc-rich ol {
                    margin: 0 0 6px 0;
                    padding-left: 20px;
                }
                .interv-desc-rich li {
                    margin: 0 0 2px 0;
                }
                .interv-desc-rich b,
                .interv-desc-rich strong {
                    font-weight: 700;
                }
                .interv-desc-rich i,
                .interv-desc-rich em,
                .interv-desc-rich cite {
                    font-style: italic;
                }
                .interv-desc-rich u,
                .interv-desc-rich ins {
                    text-decoration: underline;
                }
                .interv-desc-rich sub,
                .interv-desc-rich sup {
                    font-size: 75%;
                    line-height: 0;
                }
                .interv-desc-link {
                    color: #1a6fb5;
                    text-decoration: underline;
                    word-break: break-all;
                    overflow-wrap: break-word;
                }
                @media (max-width: 768px) {
                    .summ-client-row {
                        font-size: 20px;
                    }
                    .summ-meta-row {
                        gap: 10px;
                    }
                    .summ-section-title {
                        font-size: 16px;
                    }
                    .summ-finding-label {
                        font-size: 14px;
                    }
                }
                .grv-selection-list {
                    border: 1px solid #d5dbe0;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 20px;
                }
                .grv-selection-option {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 10px 14px;
                    font-size: 14px;
                    line-height: 1.5;
                    cursor: pointer;
                    white-space: normal;
                    word-break: break-word;
                    border-bottom: 1px solid #eef0f2;
                }
                .grv-selection-option:last-child {
                    border-bottom: none;
                }
                .grv-selection-option input[type="checkbox"] {
                    margin: 3px 0 0;
                    flex-shrink: 0;
                    width: 16px;
                    height: 16px;
                }
                .grv-selection-option span {
                    flex: 1;
                    text-align: left;
                }
                .grv-sub-title {
                    font-weight: 700;
                    color: #247A33;
                    margin: 14px 0 10px;
                    font-size: 14px;
                }
                .grv-counters-3col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                    margin-bottom: 4px;
                }
                .grv-counters-1col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                }
                .grv-counter-field {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    max-width: 400px;
                    margin-bottom: 20px;
                }
                .grv-counter-field label {
                    font-size: 13px;
                    color: #247A33;
                    font-weight: 700;
                    display: inline-block;
                }
                .grv-counter1 {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .grv-counter1 button {
                    background: #0063A5;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    width: 28px;
                    height: 39px;
                    box-sizing: border-box;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: bold;
                    padding: 0;
                }
                .grv-counter1 button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .grv-counter1 button:first-child { border-top-right-radius: 0; border-bottom-right-radius: 0; }
                .grv-counter1 button:last-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }
                .grv-counter-input1 {
                    border: 1px solid #ccc;
                    border-radius: 5px;
                    background: #fff;
                    font-weight: 600;
                    color: #333;
                    width: 52px;
                    height: 39px;
                    box-sizing: border-box;
                    text-align: center;
                    font-size: 14px;
                    padding: 0;
                    font-family: inherit;
                    -moz-appearance: textfield;
                }
                .grv-counter-input1::-webkit-outer-spin-button,
                .grv-counter-input1::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .ug-dose-grid-1-col { display: grid; grid-template-columns: 1fr; gap: 25px; }
                .ug-intervention-block { margin-bottom: 0; }
                .ug-multiselect-field { position: relative; }
                .ug-multiselect { position: relative; }
                .ug-multiselect-control {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border: 1px solid #d5dbe0;
                    border-radius: 8px;
                    padding: 10px 14px;
                    background: #fff;
                    cursor: pointer;
                    font-size: 14px;
                    color: #333;
                }
                .ug-multiselect-control-text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 90%;
                }
                .ug-multiselect-arrow { color: #888; margin-left: 8px; }
                .ug-multiselect-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 14px;
    font-size: 14px;
    line-height: 1.5;
    cursor: pointer;
    white-space: normal;
    word-break: break-word;
}

.ug-multiselect-option input[type="checkbox"] {
    margin: 3px 0 0;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
}

.ug-multiselect-option:hover {
    background: #ffffff;
}

.ug-multiselect-option span {
    flex: 1;
    text-align: left;
}
                .ug-loading-text, .ug-error-text {
                    padding: 0 20px;
                    font-size: 14px;
                }
                .ug-error-text { color: #d9534f; }

                .ug-counter-input {
                    width: 16px;
                    text-align: center;
                    border: 1px solid #d5dbe0;
                    border-radius: 4px;
                    padding: 4px 2px;
                    font-size: 13px;
                    color: #333;
                }
                .ug-counter-input:focus {
                    outline: none;
                    border-color: #4caf50;
                    box-shadow: 0 0 0 1px #4caf50;
                }
                /* hide native number spinner so only our +/- buttons show */
                .ug-counter-input::-webkit-outer-spin-button,
                .ug-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .ug-counter-input[type="number"] {
                    -moz-appearance: textfield;
                }

                @keyframes ug-select-expand {
                    from { opacity: 0; transform: scaleY(0.85); }
                    to { opacity: 1; transform: scaleY(1); }
                }

                .ug-card-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.45);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }
                .ug-card-modal {
                    width: 100%;
                    max-width: 1020px;
                    max-height:500px;
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.25);
                }
                .ug-card-modal-header {
                    background: #2f7a3d;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 20px;
                }
                .ug-card-modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 700;
                }
                .ug-card-modal-close {
                    background: #2f7a3d;
                    color: #030303;
                    border: none;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    font-size: 16px;
                    line-height: 1;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ug-card-modal-body {
                    display: flex;
                    gap: 24px;
                    padding: 35px;
                    align-items: flex-start;
                    max-height: 400px;
                    overflow-y: auto;
                }
                .ug-card-modal-text {
                    flex: 1.2;
                    font-size: 14px;
                    line-height: 2.1;
                    color: #24352a;
                    text-align:justify;
                }
                .ug-card-modal-image {
                    flex: 1;
                    border: 2px solid #2f7a3d;
                    border-radius: 12px;
                    overflow: hidden;
                    height:200px;
                    width:100px;
                }
                .ug-card-modal-image img {
                    width: 100%;
                    height: auto;
                    display: block;
                }
                .ug-card-modal-footer {
                    display: flex;
                    align-items: center;
                    justify-content: right;
                    background: #d9efd9;
                    padding: 14px 24px;
                }
                .ug-card-modal-learnmore {
                    color: #2f7a3d;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                }
                .ug-card-modal-nav {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                .ug-card-modal-nav button {
                    background: #2f7a3d;
                    border: none;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    cursor: pointer;
                    color: #fff;
                    font-size: 14px;
                    transition: background 0.15s ease;
                }
                .ug-card-modal-nav button:hover {
                    background: #2f7a3d;
                }
                .ug-card-modal-nav span {
                    font-size: 13px;
                    color: #2f7a3d;
                    font-weight: 600;
                }
                .read-more {
                    color: #2f7a3d;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-block;
                    margin-top: 6px;
                }
                .description-text {
                    margin: 0;
                }
                .key-components-title {
                    font-size: 22px;
                    font-weight: 700;
                    margin-bottom: 12px;
                    margin-top: 16px;
                }

                .key-components-list {
                    padding-left: 28px;
                    margin: 0;
                }

                .key-components-list li {
                    text-align: justify;
                    line-height: 1.8;
                    margin-bottom: 12px;
                }

                .key-components-list li strong {
                    font-weight: 700;
                }

                /* PDF download: keep foundational intervention text consistent
                   with the Selected Interventions cards (14px, dark gray). */
                .ug-card-pdf-full .key-components-list,
                .ug-card-pdf-full .key-components-list li {
                    font-size: 14px;
                    line-height: 1.7;
                    color: #323C43;
                }

                .footer-text {
                    margin-top: 20px;
                    text-align: justify;
                }
                @media (max-width: 600px) {
                    .ug-card-modal-body { flex-direction: column; }
                }
            `}</style>

            {/* PDF download / generation feedback */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

        </>
    );
}
// Popup shown when a Foundational Interventions card is clicked.
// Layout/colours follow the reference design: green header with a
// close button, image on the right, description on the left, and a footer
// with page navigation that now cycles through all 5 cards via
// onPrev/onNext (passed down from the parent, which owns activeCardIndex).
const FoundationalCardModal = ({ card, cardIndex, total, onClose, onPrev, onNext }) => {
    // Reset "read more" state whenever the visible card changes, so each
    // card opens back at the collapsed/summary view.
    const [showFullDescription, setShowFullDescription] = useState(false);

    useEffect(() => {
        setShowFullDescription(false);
    }, [cardIndex]);

    if (!card) return null;

    return (
        <div className="ug-card-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={card.title}>
            <div className="ug-card-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ug-card-modal-header">
                    <h3 style={{color:"#ffffff"}}>{card.title}</h3>
                    <button className="ug-card-modal-close" onClick={onClose} aria-label="Close dialog">
                        &#10005;
                    </button>
                </div>
                <div className="ug-card-modal-body">
                    <div className="ug-card-modal-text">
                        <p className={showFullDescription ? "description-text expanded" : "description-text"}>{card.description}</p>
                        {!showFullDescription && (
                            <button
                                className="read-more"
                                onClick={() => setShowFullDescription(true)}
                                style={{ background: "none", border: "none", padding: 0, font: "inherit" }}
                                aria-expanded="false"
                            >
                                Read More...
                            </button>
                        )}
                        {showFullDescription && (
                            <>
                                <div className="key-components-title">
                                    Key components
                                </div>

                                <ul className="key-components-list">
                                    {card.keyComponents.map((item, i) => (
                                        <li key={i}>
                                            <strong>{item.title}:</strong> {item.text}
                                        </li>
                                    ))}
                                </ul>

                                {card.footer && (
                                    <p className="footer-text">
                                        {card.footer}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    <div className="ug-card-modal-image">
                        <img src={card.image} alt={card.title} />
                    </div>
                </div>
                <div className="ug-card-modal-footer">

                    <div className="ug-card-modal-nav">
                        <button onClick={onPrev} aria-label="Previous">&#10094;</button>
                        <span>{cardIndex + 1}/{total}</span>
                        <button onClick={onNext} aria-label="Next">&#10095;</button>
                    </div>
                </div>
            </div>
        </div>
    );
};
